import { useMemo, useState } from 'react'

import Button from 'common/components/button'
import ConfirmationModal from 'common/components/confirmationModal'
import { Column } from 'common/components/table'
import { SingleSelectCell } from 'common/components/table/cells'
import BaseCell from 'common/components/table/cells/Base'
import { generateDeleteButtonColumn } from 'common/components/table/columns'
import Tags from 'common/components/tags'
import TableWidget from 'common/components/widget/tableWidget'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { useFocusEditableCell } from 'common/hooks/table'
import { ReactComponent as AddIcon } from 'common/icons/add/default.svg'
import { PART_OF_SPEACH_OPTIONS } from 'common/partOfSpeach'

import {
  addSynonymRow,
  deleteSynonymRow,
  updateSynonymCell,
} from '../store/contentSettingsSlice'

type Row = {
  id: number
  headToken: string
  partOfSpeech: string
  isInherited: boolean
  output: string[] | { value: string; label: string; isDisabled: boolean }[]
}

const SynonymsWidget = () => {
  const synonyms = useAppSelector(
    (state) => state.contentSettings.editedContent.synonyms
  )

  const dispatch = useAppDispatch()
  const {
    TextEditableCellWithFocus,
    editableCellFocus,
    editableCellLoseFocus,
  } = useFocusEditableCell()

  const [rowIdToDelete, setRowIdToDelete] =
    useState<number | undefined>(undefined)
  const [resetFilterKey, setResetFilterKey] = useState(0)

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        accessor: 'headToken',
        disableSortBy: true,
        Cell: (cellProps) =>
          cellProps.row.original.isInherited ? (
            <BaseCell>{cellProps.row.original.headToken}</BaseCell>
          ) : (
            <TextEditableCellWithFocus
              {...cellProps}
              onCellChange={(cellProps as any).onCellChange}
            />
          ),
        Header: 'Head token',
        width: 300,
        isResizable: true,
        minWidth: 150,
        maxWidth: 190,
      },
      {
        accessor: 'partOfSpeech',
        disableSortBy: true,
        Header: 'Part of speech',
        Cell: (cellProps) => {
          return (
            <BaseCell className="w-full">
              {cellProps.row.original.isInherited ? (
                <div>
                  {
                    PART_OF_SPEACH_OPTIONS.find(
                      ({ value }) => value === cellProps.value
                    )?.label
                  }
                </div>
              ) : (
                <SingleSelectCell<Row>
                  row={cellProps.row}
                  column={cellProps.column}
                  value={cellProps.value}
                  onCellChange={cellProps.onCellChange}
                  singleSelectProps={{
                    options: PART_OF_SPEACH_OPTIONS,
                  }}
                />
              )}
            </BaseCell>
          )
        },
        maxWidth: 1,
        minWidth: 151,
      },
      {
        accessor: 'output',
        disableSortBy: true,
        Header: 'Output',
        Cell: ({ value, row }) => {
          return (
            <div className="ml-6 my-4 flex items-center">
              <Tags
                data-cy="synonym-output"
                isFreeText
                onAddClick={(value: string) => {
                  dispatch(
                    updateSynonymCell({
                      value: [
                        ...row.original.output,
                        {
                          value: value.toLowerCase(),
                          label: value.toLowerCase(),
                          isInherited: false,
                        },
                      ],
                      rowIndex: row.index,
                      columnId: 'output',
                    })
                  )
                  setResetFilterKey((prev) => prev + 1)
                }}
                onRemoveClick={(value: string) => {
                  dispatch(
                    updateSynonymCell({
                      value: (
                        row.original.output as {
                          value: string
                          label: string
                          isDisabled: boolean
                        }[]
                      ).filter((item: any) => item.value !== value),
                      rowIndex: row.index,
                      columnId: 'output',
                    })
                  )
                  setResetFilterKey((prev) => prev + 1)
                }}
                onInputChange={(value: string) => value.replaceAll(' ', '')}
                tags={value.map((value) => ({
                  value: value.value,
                  label: value.value,
                  isDisabled: value.isInherited,
                }))}
                maxLength={50}
              />
            </div>
          )
        },
        width: 400,
      },
      generateDeleteButtonColumn<Row>({
        onClick: (row) => {
          setRowIdToDelete(row.id)
        },
        canDelete: (row) => !row.isInherited,
        buttonLabel: 'Delete',
      }),
    ],
    [TextEditableCellWithFocus, dispatch]
  )
  const handleDeleteCancel = () => {
    setRowIdToDelete(undefined)
  }

  const handleDeleteConfirm = () => {
    setRowIdToDelete(undefined)
    if (rowIdToDelete !== undefined) {
      dispatch(deleteSynonymRow(rowIdToDelete))
    }
  }

  return (
    <>
      <TableWidget.Widget
        data-cy="synonyms-widget"
        columns={columns}
        data={synonyms}
        firstUseText="No synonyms added yet"
        onCellChange={({ rowIndex, columnId, value }) => {
          dispatch(updateSynonymCell({ rowIndex, columnId, value }))
          editableCellLoseFocus()
          setResetFilterKey((prev) => prev + 1)
        }}
      >
        <TableWidget.Header
          title="Synonyms"
          subtitle="Some short guideance text can go here..."
        >
          <Button
            className="mr-4"
            variant="primary"
            data-cy="add-synonyms"
            prefixIcon={<AddIcon width={24} height={24} />}
            onClick={() => {
              dispatch(addSynonymRow())
              editableCellFocus()
              setResetFilterKey((prev) => prev + 1)
            }}
          >
            Synonym
          </Button>
          <TableWidget.Filter key={resetFilterKey} />
        </TableWidget.Header>
      </TableWidget.Widget>
      <ConfirmationModal
        open={rowIdToDelete !== undefined}
        title="Remove synonyms row!"
        data-cy="synonyms-row-delete-modal"
        confirmationText="Are you sure you want to remove this row?"
        onCancel={() => handleDeleteCancel()}
        onConfirm={() => handleDeleteConfirm()}
      />
    </>
  )
}

export default SynonymsWidget
